import { EventParticipant } from "@models/EventParticipant";
import { Stop } from "@models/Stop";
import { Ref } from "vue";
import { Location } from "@models/Location";
import * as turf from "@turf/turf";

export default function useGeoFencing(): {
  watchLocations: (
    stops: Stop[],
    eventParticipants: Ref<EventParticipant[]>,
    enteredCallback: (location: any, checkinMetadata: any) => void
  ) => void;
  cleanupWatch: () => void;
} {
  let watchId: number = 0;
  const options = {
    enableHighAccuracy: true,
    maximumAge: 0,
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const from = turf.point([lat1, lon1]);
    const to = turf.point([lat2, lon2]);
    return turf.distance(from, to, {units: "meters"});
  };

  const isCheckedIn = (eventParticipants: Ref<EventParticipant[]>, stop: Stop) =>
    eventParticipants.value.some(
      (eventParticipant) =>
        eventParticipant.stops.find((x) => x.order === stop.order)?.checkedIn ?? false
    );

  const canCheckIn = (stop: Stop) => {
     const expectedArrivalTime = new Date(stop.expectedArrivalTime as string);
    const MS_PER_MINUTE = 60000;
    const checkingStartTime = new Date(expectedArrivalTime.getTime() - 15 * MS_PER_MINUTE);
    const afterCheckinTime = checkingStartTime < new Date();
    return afterCheckinTime;
  }
  
  const watchLocations = (
    stops: Stop[],
    eventParticipants: Ref<EventParticipant[]>,
    enteredCallback: (stop: Stop, checkinMetadata: any) => void
  ) => {
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        let found = false;
        // Check if the user is inside any geofence
        stops.forEach((stop: Stop) => {
          if (!stop.address?.location || found || isCheckedIn(eventParticipants, stop)) {
            return;
          }
          
          const distance = calculateDistance(
            position.coords.latitude,
            position.coords.longitude,
            stop.address.location._lat,
            stop.address.location._long
          );
          
          const checkinMetadata = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            distance: distance
          }
          if (distance < 50 && canCheckIn(stop)) {
            // User is inside the geofence
            enteredCallback(stop, checkinMetadata);
            found = true;
          } 
        });
      },
      (error) => {
        // Handle any errors here
      },
      options
    );
  };
  const cleanupWatch = () => (watchId !== 0 ? navigator.geolocation.clearWatch(watchId) : null);

  return { watchLocations, cleanupWatch };
}
